import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { CreditosModel } from '../../../../../modules/finanzas/creditos/creditos.model';
import { CreditosConEmpleadosModel } from '../../../../../modules/finanzas/creditos-con-empleados/creditos-con-empleados.model';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  entityFormDialogConfig,
  EntityFormOption,
  ensureOption,
  findOption,
  fromApiDate,
  isBlank,
  isInvalidMonths,
  isMissingNumber,
  numberOrNull,
  optionId,
  serverErrorMessage,
  SETTLEMENT_OPTIONS,
  TERM_LONG,
  TERM_SHORT,
  toApiDate,
} from '../../../entity-form/entity-form.utils';
import { CreditKind } from '../../models/credit-shared.model';
import { CreditEntityService } from '../../services/credit-entity.service';
import { EmployeeCreditEntityService } from '../../services/employee-credit-entity.service';
import { CreditFormLookupsFacade } from '../../services/credit-form-lookups.facade';

export interface CreditFormDialogData {
  kind: CreditKind;
  uid?: string;
}

const SHORT_TERM_ACCOUNTS: EntityFormOption[] = [
  { id: '5323', display: 'Créditos a corto plazo a empresas del grupo - 5323' },
  { id: '5324', display: 'Créditos a corto plazo a empresas asociadas - 5324' },
  { id: '5325', display: 'Créditos a corto plazo a otras partes vinculadas - 5325' },
];

const LONG_TERM_ACCOUNTS: EntityFormOption[] = [
  { id: '2423', display: 'Créditos a largo plazo a empresas del grupo - 2423' },
  { id: '2424', display: 'Créditos a largo plazo a empresas asociadas - 2424' },
  { id: '2425', display: 'Créditos a largo plazo a otras partes vinculadas - 2425' },
];

const TERM_OPTIONS: EntityFormOption[] = [
  { id: TERM_SHORT, display: 'Corto plazo' },
  { id: TERM_LONG, display: 'Largo plazo' },
];

type CreditFormErrors = Partial<Record<
  'employee' | 'duracion' | 'cuentaContableACrear' | 'descripcion' | 'fechaConstitucion' | 'plazo' | 'fechaFinalizacion'
  | 'carencia' | 'importeInicial' | 'importePendiente' | 'tipoInteres' | 'tipoLiquidaciones' | 'bankAccount', string>>;

@Component({
  selector: 'app-credit-form-dialog',
  templateUrl: './credit-form-dialog.component.html',
  styleUrls: ['../../../entity-form/entity-form-dialog.scss'],
  standalone: false,
})
export class CreditFormDialogComponent implements OnInit, OnDestroy {
  readonly settlementOptions = [...SETTLEMENT_OPTIONS];
  readonly termOptions = [...TERM_OPTIONS];

  private readonly shortTermAccounts = [...SHORT_TERM_ACCOUNTS];
  private readonly longTermAccounts = [...LONG_TERM_ACCOUNTS];
  private readonly unknownAccounts: EntityFormOption[] = [];

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: CreditFormErrors = {};

  bankAccounts: EntityFormOption[] = [];
  employees: EntityFormOption[] = [];

  private loaded: any = null;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: CreditFormDialogData): Observable<boolean> {
    return dialog.open<CreditFormDialogComponent, CreditFormDialogData, boolean>(CreditFormDialogComponent, entityFormDialogConfig(data))
      .afterClosed()
      .pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreditFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CreditFormDialogData,
    private creditEntity: CreditEntityService,
    private employeeCreditEntity: EmployeeCreditEntityService,
    private lookups: CreditFormLookupsFacade,
    private notify: NotificationService,
  ) {
    this.form = this.fb.group({
      employee: [null],
      duracion: [null],
      cuentaContableACrear: [null],
      descripcion: [''],
      fechaConstitucion: [null],
      plazo: [null],
      fechaFinalizacion: [null],
      carencia: [null],
      importeInicial: [null],
      importePendiente: [null],
      tipoInteres: [null],
      tipoLiquidaciones: [null],
      bankAccount: [null],
    });
  }

  get isEmployee(): boolean {
    return this.data.kind === 'employee';
  }

  get isEdit(): boolean {
    return !!this.data.uid;
  }

  get title(): string {
    if (this.isEmployee) return this.isEdit ? 'Editar crédito con empleado' : 'Nuevo crédito con empleado';
    return this.isEdit ? 'Editar crédito' : 'Nuevo crédito a otra entidad';
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return (this.isEmployee ? this.loaded.employeesName : this.loaded.descripcion) || 'Modifica los datos del crédito';
    }
    return this.isEmployee ? 'Registra un crédito concedido a un empleado' : 'Registra un crédito concedido a otra entidad';
  }

  get icon(): string {
    return this.isEmployee ? 'person' : 'trending_up';
  }

  get accountOptions(): EntityFormOption[] {
    const duracion = Number(optionId(this.form.get('duracion').value));
    if (duracion === TERM_SHORT) return this.shortTermAccounts;
    if (duracion === TERM_LONG) return this.longTermAccounts;
    return this.unknownAccounts;
  }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  get employeeControl(): UntypedFormControl {
    return this.control('employee');
  }

  get duracionControl(): UntypedFormControl {
    return this.control('duracion');
  }

  get accountControl(): UntypedFormControl {
    return this.control('cuentaContableACrear');
  }

  get descripcionControl(): UntypedFormControl {
    return this.control('descripcion');
  }

  get bankAccountControl(): UntypedFormControl {
    return this.control('bankAccount');
  }

  ngOnInit(): void {
    this.form.get('duracion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const account = optionId(this.accountControl.value);
      if (account !== null && !findOption(this.accountOptions, account)) {
        this.accountControl.setValue(null);
      }
    });
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const request = this.isEmployee
      ? (this.isEdit ? this.employeeCreditEntity.update(this.data.uid, payload) : this.employeeCreditEntity.create(payload))
      : (this.isEdit ? this.creditEntity.update(this.data.uid, payload) : this.creditEntity.create(payload));

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(this.isEdit ? 'Crédito actualizado correctamente' : 'Crédito creado correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = serverErrorMessage(error, this.isEdit
          ? 'Ha ocurrido un error inesperado al actualizar el crédito'
          : 'Ha ocurrido un error inesperado al crear el crédito');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private load(): void {
    this.ready = false;
    const record: Promise<any> = !this.isEdit
      ? Promise.resolve(null)
      : this.isEmployee ? this.employeeCreditEntity.get(this.data.uid) : this.creditEntity.get(this.data.uid);
    const employees = this.isEmployee ? this.lookups.employees() : Promise.resolve([] as EntityFormOption[]);

    Promise.all([record, this.lookups.bankAccounts(), employees])
      .then(([credit, bankAccounts, employeeList]) => {
        this.bankAccounts = bankAccounts;
        this.employees = employeeList;
        this.loaded = credit;
        if (credit) this.applyRecord(credit);
        this.ready = true;
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(credit: any): void {
    const employee = this.isEmployee
      ? ensureOption(this.employees, credit.employeesId, credit.employeesName)
      : null;

    this.form.patchValue({
      employee,
      duracion: this.isEmployee ? null : ensureOption(this.termOptions, numberOrNull(credit.duracion)),
      cuentaContableACrear: null,
      descripcion: credit.descripcion || '',
      fechaConstitucion: fromApiDate(credit.fechaConstitucion),
      plazo: numberOrNull(credit.plazo),
      fechaFinalizacion: fromApiDate(credit.fechaFinalizacion),
      carencia: numberOrNull(credit.carencia),
      importeInicial: numberOrNull(credit.importeInicial),
      importePendiente: numberOrNull(credit.importePendiente),
      tipoInteres: numberOrNull(credit.tipoInteres),
      tipoLiquidaciones: ensureOption(this.settlementOptions, numberOrNull(credit.tipoLiquidaciones)),
      bankAccount: ensureOption(this.bankAccounts, credit.bankAccountsUid, credit.bankAccountInfo),
    }, { emitEvent: false });

    if (!this.isEmployee) {
      const account = ensureOption(this.accountOptions, credit.cuentaContableACrear);
      this.accountControl.setValue(account, { emitEvent: false });
    }
  }



  private validate(): CreditFormErrors {
    const value = this.form.getRawValue();
    const errors: CreditFormErrors = {};

    if (this.isEmployee) {
      if (optionId(value.employee) === null) errors.employee = 'Selecciona un empleado de la lista';
    } else {
      if (optionId(value.duracion) === null) errors.duracion = 'Selecciona la duración';
      if (optionId(value.cuentaContableACrear) === null) errors.cuentaContableACrear = 'Selecciona la cuenta contable';
      if (isBlank(value.descripcion)) errors.descripcion = 'Indica la descripción';
    }

    if (!value.fechaConstitucion) errors.fechaConstitucion = 'Indica la fecha de constitución';
    if (isInvalidMonths(value.plazo)) errors.plazo = 'Indica el plazo en meses';
    if (!value.fechaFinalizacion) errors.fechaFinalizacion = 'Indica la fecha de finalización';
    if (isInvalidMonths(value.carencia)) errors.carencia = 'Indica la carencia en meses';
    if (isMissingNumber(value.importeInicial)) errors.importeInicial = 'Indica el importe inicial';
    if (isMissingNumber(value.importePendiente)) errors.importePendiente = 'Indica el importe pendiente';
    if (isMissingNumber(value.tipoInteres)) errors.tipoInteres = 'Indica el tipo de interés';
    if (optionId(value.tipoLiquidaciones) === null) errors.tipoLiquidaciones = 'Selecciona el tipo de liquidaciones';
    if (optionId(value.bankAccount) === null) errors.bankAccount = 'Selecciona la cuenta de abono';

    return errors;
  }

  private buildPayload(): any {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    const common = {
      fechaConstitucion: toApiDate(value.fechaConstitucion),
      plazo: Number(value.plazo),
      fechaFinalizacion: toApiDate(value.fechaFinalizacion),
      carencia: Number(value.carencia),
      importeInicial: Number(value.importeInicial),
      importePendiente: Number(value.importePendiente),
      tipoInteres: Number(value.tipoInteres),
      tipoLiquidaciones: Number(optionId(value.tipoLiquidaciones)),
      bankAccountsUid: optionId(value.bankAccount),
    };

    if (this.isEmployee) {
      const employee = value.employee;
      const payload: CreditosConEmpleadosModel = {
        ...base,
        ...common,
        employeesId: Number(employee.id),
        employeesName: employee.display,
      };
      return payload;
    }

    const payload: CreditosModel = {
      ...base,
      ...common,
      descripcion: String(value.descripcion).trim(),
      duracion: Number(optionId(value.duracion)),
      cuentaContableACrear: String(optionId(value.cuentaContableACrear)),
    };
    if (this.loaded && Number(this.loaded.duracion) !== payload.duracion) {
      payload.accountingModelsCortoPlazoNumber = null;
      payload.accountingModelsLargoPlazoNumber = null;
    }
    return payload;
  }
}
